import { useSyncExternalStore } from "react";

import type { WindowRect } from "@frontend/components/molecules/Window";

import {
  COLLAPSED_STORAGE_KEY,
  OPEN_MODE_STORAGE_KEY,
  WINDOW_RECTS_STORAGE_KEY,
  WINDOW_SIDEBAR_WIDTH_STORAGE_KEY,
} from "./constants";

// Another profile opened from a window: its own window, or a tab of the
// window it was opened from.
export type OpenMode = "window" | "tab";

const listeners = new Set<() => void>();

function read<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);

    return null === raw ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A full or forbidden storage forgets the preference, nothing more.
  }

  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

let mode: OpenMode | null = null;

export function useOpenMode(): [OpenMode, (mode: OpenMode) => void] {
  const value = useSyncExternalStore(subscribe, () => {
    mode ??=
      "tab" === read<string>(OPEN_MODE_STORAGE_KEY, "window")
        ? "tab"
        : "window";

    return mode;
  });

  return [
    value,
    (next) => {
      mode = next;
      write(OPEN_MODE_STORAGE_KEY, next);
    },
  ];
}

// Where each window sits, by its rank on the page: the first window opens
// where the first one was left, and so on.
export function readWindowRect(rank: number): WindowRect | undefined {
  return (
    read<Array<WindowRect | null>>(WINDOW_RECTS_STORAGE_KEY, [])[rank] ??
    undefined
  );
}

export function writeWindowRect(rank: number, rect: WindowRect) {
  const rects = read<Array<WindowRect | null>>(WINDOW_RECTS_STORAGE_KEY, []);
  rects[rank] = rect;
  write(WINDOW_RECTS_STORAGE_KEY, rects);
}

// The pill folded into a dot on the edge, for this tab only: a page whose
// button it covers, then back to normal on the next visit.
let collapsed: boolean | null = null;

export function useCollapsed(): [boolean, (collapsed: boolean) => void] {
  const value = useSyncExternalStore(subscribe, () => {
    if (null === collapsed) {
      try {
        collapsed =
          "1" === window.sessionStorage.getItem(COLLAPSED_STORAGE_KEY);
      } catch {
        collapsed = false;
      }
    }

    return collapsed;
  });

  return [
    value,
    (next) => {
      collapsed = next;

      try {
        if (next) {
          window.sessionStorage.setItem(COLLAPSED_STORAGE_KEY, "1");
        } else {
          window.sessionStorage.removeItem(COLLAPSED_STORAGE_KEY);
        }
      } catch {
        // Without storage the fold lasts until the page reloads.
      }

      for (const listener of listeners) {
        listener();
      }
    },
  ];
}

// The width of the collectors' sidebar in a window, shared by every
// window of the page and kept per browser
export const WINDOW_SIDEBAR_WIDTH = { min: 140, max: 360, initial: 208 };

let sidebarWidth: number | null = null;

export function useWindowSidebarWidth(): [number, (width: number) => void] {
  const value = useSyncExternalStore(subscribe, () => {
    sidebarWidth ??= Math.min(
      WINDOW_SIDEBAR_WIDTH.max,
      Math.max(
        WINDOW_SIDEBAR_WIDTH.min,
        read<number>(
          WINDOW_SIDEBAR_WIDTH_STORAGE_KEY,
          WINDOW_SIDEBAR_WIDTH.initial,
        ),
      ),
    );

    return sidebarWidth;
  });

  return [
    value,
    (next) => {
      sidebarWidth = next;
      write(WINDOW_SIDEBAR_WIDTH_STORAGE_KEY, next);
    },
  ];
}
